import type { Patient } from '@/lib/clinic/types'

import { phoneDigits } from '@/lib/clinic/format'
import { parseCodeNumber } from '@/lib/clinic/ids'

interface ScoredPatient {
    patient: Patient
    score: number
}

export const scorePatient = (patient: Patient, query: string): number => {
    const q = query.trim().toLowerCase()
    if (!q) return 0

    const digits = q.replace(/\D/g, '')
    const codeMatch = q.match(/^(?:dsc)?[\s-]*0*(\d+)$/)
    const codeNumber = codeMatch ? Number(codeMatch[1]) : null
    const tokens = q.split(/\s+/).filter(Boolean)
    const isPhoneQuery = !/[a-z]/.test(q)
    const phoneQuery = digits.length > 10 ? digits.slice(-10) : digits

    const name = patient.name.toLowerCase()
    const words = name.split(/\s+/)
    const code = patient.code.toLowerCase()
    const phone = phoneDigits(patient.phone)

    let score = 0

    if (codeNumber !== null && parseCodeNumber(patient.code) === codeNumber) score = Math.max(score, 100)
    if (code.includes(q)) score = Math.max(score, 90)

    if (name.startsWith(q)) score = Math.max(score, 85)
    else if (tokens.every(token => words.some(word => word.startsWith(token)))) score = Math.max(score, 75)
    else if (name.includes(q)) score = Math.max(score, 55)

    if (isPhoneQuery && phoneQuery.length >= 3 && phone.includes(phoneQuery)) {
        score = Math.max(score, phone.endsWith(phoneQuery) ? 80 : 70)
    }

    return score
}

export const searchPatients = (patients: Patient[], query: string, limit = 8): Patient[] => {
    if (!query.trim()) return []

    const scored: ScoredPatient[] = []

    for (const patient of patients) {
        const score = scorePatient(patient, query)
        if (score > 0) scored.push({ patient, score })
    }

    scored.sort((a, b) => b.score - a.score || a.patient.name.localeCompare(b.patient.name))

    return scored.slice(0, limit).map(item => item.patient)
}

export const matchesText = (query: string, fields: Array<string | null | undefined>): boolean => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (tokens.length === 0) return true

    const haystack = fields.filter(Boolean).join(' ').toLowerCase()

    return tokens.every(token => haystack.includes(token))
}
